// Two-pane bottom sheet for the PDP — size guide and returns policy.
//
// Deliberately not built on the theme's DialogComponent (assets/dialog.js):
// that one calls showModal() and sets `document.body { position: fixed }` to
// hold the page still, which fights this page's own scroll lock
// (`body:has(.shabibi-pdp) { overflow: hidden }` plus a fixed-height hero)
// and produces a visible jump on open. The PDP already can't scroll, so
// there is nothing to freeze — the sheet only needs stacking, focus
// management and dismissal.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Past this much downward travel, release the sheet instead of snapping back.
const DISMISS_DISTANCE = 90;
// …or below that distance if the flick was fast enough to read as intent.
const DISMISS_VELOCITY = 0.5;

class ShabibiSizeGuide extends HTMLElement {
  constructor() {
    super();
    this.sheet = null;
    this.scrim = null;
    this.panes = [];
    this.isOpen = false;
    this.lastFocused = null;
    this.dragStartY = 0;
    this.dragStartTime = 0;
    this.dragOffset = 0;
    this.isDragging = false;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleEditorBlockSelect = this.handleEditorBlockSelect.bind(this);
    this.handleEditorBlockDeselect = this.handleEditorBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.sheet = this.querySelector('[data-sheet]');
    this.scrim = this.querySelector('[data-sheet-scrim]');
    if (!this.sheet) return;

    this.panes = Array.from(this.querySelectorAll('[data-sheet-pane]'));

    this.scrim?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-sheet-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    this.initDragToDismiss();

    // Triggers live outside this element (in the size pill and the trust
    // line), so they're bound at the document rather than locally.
    document.addEventListener('click', this.handleDocumentClick);

    // Theme editor only: the "Size guide image" blocks render inside this
    // sheet, which is hidden until opened, so selecting one in the editor
    // sidebar would otherwise highlight nothing visible.
    document.addEventListener('shopify:block:select', this.handleEditorBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleEditorBlockDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleEditorBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleEditorBlockDeselect);
  }

  handleEditorBlockSelect(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const pane = event.target.closest('[data-sheet-pane]');
    if (!this.isOpen) this.open(pane?.dataset.sheetPane);
  }

  handleEditorBlockDeselect(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.close();
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-sheet-open]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger.dataset.sheetOpen, trigger);
  }

  /**
   * @param {string} pane - Which pane to show ('size-guide' | 'returns').
   * @param {HTMLElement} [trigger] - Element focus returns to on close.
   */
  open(pane, trigger) {
    if (!this.sheet) return;

    this.showPane(pane);
    this.lastFocused = trigger || document.activeElement;

    this.hidden = false;
    // Two frames, so the browser has actually computed the closed position
    // before the open one is applied. With one frame the style change can
    // still be coalesced with the display change and the sheet appears
    // already-open instead of rising.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('is-open'));
    });

    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.handleKeydown);

    const first = this.sheet.querySelector(FOCUSABLE);
    if (first instanceof HTMLElement) first.focus();
  }

  close() {
    if (!this.isOpen || !this.sheet) return;

    this.classList.remove('is-open');
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.handleKeydown);

    // Any drag offset left on the sheet has to be cleared or the next open
    // starts partway down.
    this.sheet.style.transform = '';

    const onDone = () => {
      if (!this.isOpen) this.hidden = true;
      this.sheet.removeEventListener('transitionend', onDone);
    };
    this.sheet.addEventListener('transitionend', onDone);

    if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
  }

  showPane(name) {
    const target = name || 'size-guide';
    this.panes.forEach((pane) => {
      pane.hidden = pane.dataset.sheetPane !== target;
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Focus trap. The sheet sits over a page that is still in the DOM and
    // still focusable, so without this, tabbing walks out of the sheet and
    // into content the user can't see.
    // getClientRects() rather than offsetParent: offsetParent is null for
    // position: fixed elements regardless of whether they're visible, which
    // would silently drop them from the trap.
    const focusable = Array.from(this.sheet.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  initDragToDismiss() {
    const handle = this.querySelector('[data-sheet-handle]') || this.sheet;

    handle.addEventListener(
      'touchstart',
      (e) => {
        this.dragStartY = e.touches[0].clientY;
        this.dragStartTime = e.timeStamp;
        this.dragOffset = 0;
        this.isDragging = true;
        this.sheet.style.transition = 'none';
      },
      { passive: true }
    );

    handle.addEventListener(
      'touchmove',
      (e) => {
        if (!this.isDragging) return;
        const delta = e.touches[0].clientY - this.dragStartY;
        // Downward only — dragging up shouldn't lift the sheet off its stop.
        this.dragOffset = Math.max(0, delta);
        this.sheet.style.transform = `translateY(${this.dragOffset}px)`;
      },
      { passive: true }
    );

    const endDrag = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.sheet.style.transition = '';

      const elapsed = Math.max(1, e.timeStamp - this.dragStartTime);
      const velocity = this.dragOffset / elapsed;

      if (this.dragOffset > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY) {
        this.close();
      } else {
        this.sheet.style.transform = '';
      }
    };

    handle.addEventListener('touchend', endDrag);
    handle.addEventListener('touchcancel', endDrag);
  }
}

if (!customElements.get('shabibi-size-guide')) {
  customElements.define('shabibi-size-guide', ShabibiSizeGuide);
}
